import type { ITabPanelModule } from '../types';

/**
 * Hilfsfunktion für Debouncing
 * @param mainFunction - Die zu verzögernde Funktion
 * @param delay - Verzögerung in Millisekunden
 * @returns Die debounced Funktion
 */
function debounce<T extends (...args: unknown[]) => void>(
    mainFunction: T,
    delay: number
): (...args: Parameters<T>) => void {
    let timer: ReturnType<typeof setTimeout> | undefined;

    return function (this: unknown, ...args: Parameters<T>): void {
        if (timer !== undefined) {
            clearTimeout(timer);
        }

        timer = setTimeout(() => {
            mainFunction.apply(this, args);
        }, delay);
    };
}

/**
 * Modul für Tab-Panel-Paginierung
 * Verwaltet die Anzeige von Tabs mit Vor-/Zurück-Navigation
 */
const TabPanelModule: ITabPanelModule = {
    /**
     * Initialisiert die Tab-Panel-Paginierung
     * @param tabPanelId - Die ID des Tab-Panels
     * @param previousTabButtonId - Die ID des "Vorheriger Tab"-Buttons
     * @param nextTabButtonId - Die ID des "Nächster Tab"-Buttons
     * @param plusTabButtonId - Die ID des "Plus Tab"-Buttons
     */
    initTabPanelPagination(
        tabPanelId: string,
        previousTabButtonId: string,
        nextTabButtonId: string,
        plusTabButtonId: string
    ): void {
        let tabOffset = 0;
        let tabLimit = 0;

        const previousTabButton = document.getElementById(previousTabButtonId);
        if (!previousTabButton) {
            console.warn(`Previous tab button with id "${previousTabButtonId}" not found`);
            return;
        }

        const nextTabButton = document.getElementById(nextTabButtonId);
        if (!nextTabButton) {
            console.warn(`Next tab button with id "${nextTabButtonId}" not found`);
            return;
        }

        const plusTabButton = document.getElementById(plusTabButtonId);
        let tabDivider: HTMLElement|null = null;

        if (plusTabButton !== null) {
            tabDivider = plusTabButton.querySelector<HTMLElement>('.tab-divider');
        }

        const tabPanel = document.getElementById(tabPanelId);
        if (!tabPanel || !tabPanel.firstChild) {
            console.warn(`Tab panel with id "${tabPanelId}" not found or empty`);
            return;
        }

        const tabPanelButtons = tabPanel.firstChild as HTMLElement;

        // Ab hier sind previousTabButton und nextTabButton garantiert nicht null
        const navButtonWidth = previousTabButton.getBoundingClientRect().width;
        previousTabButton.style.display = 'none';
        nextTabButton.style.display = 'none';

        // Lokale Referenzen für Closure-Zugriff (TypeScript Control Flow Analysis)
        const prevButton = previousTabButton;
        const nxtButton = nextTabButton;

        /**
         * Ordnet das Tab-Panel neu an und zeigt/versteckt Tabs basierend auf verfügbarem Platz
         */
        function rearrangeTabPanel(): void {
            const tabs = Array.from(tabPanelButtons.querySelectorAll<HTMLElement>('[role="tab"]'));

            let tabPanelButtonsWidth = tabPanelButtons.getBoundingClientRect().width;
            tabPanelButtonsWidth = Math.min(
                window.innerWidth - tabPanelButtons.getBoundingClientRect().x,
                tabPanelButtonsWidth
            );

            prevButton.style.display = tabOffset > 0 ? 'flex' : 'none';

            let tabsSumWidth = 0;

            if (tabOffset > 0) {
                tabsSumWidth += navButtonWidth;
            }

            if (plusTabButton !== null) {
                tabsSumWidth += plusTabButton.getBoundingClientRect().width;
            }
            
            let index = 0;
            let endFound = false;
            let newLimit = 0;

            for (const tab of tabs) {
                let showTab = index >= tabOffset && !endFound;
                index++;

                if (showTab) {
                    tab.style.display = 'flex';
                    const tabWidth = tab.getBoundingClientRect().width;
                    if (newLimit > 0 && tabsSumWidth + tabWidth + navButtonWidth > tabPanelButtonsWidth) {
                        endFound = true;
                        showTab = false;
                    }

                    if (showTab) {
                        newLimit++;
                        tabsSumWidth += tabWidth;
                    }
                }

                tab.style.display = showTab ? 'flex' : 'none';
            }

            tabLimit = newLimit;
            const isNextButtonVisible = tabOffset + tabLimit < tabs.length;
            nxtButton.style.display = isNextButtonVisible ? 'flex' : 'none';
            
            // Tab-Divider anzeigen/verstecken basierend auf der Sichtbarkeit des Next-Buttons
            if (tabDivider !== null) {
                tabDivider.style.display = isNextButtonVisible ? 'inline-block' : 'none';
            }
        }

        // Event-Listener für Previous-Button
        prevButton.addEventListener('click', () => {
            tabOffset--;
            rearrangeTabPanel();
        });

        // Event-Listener für Next-Button
        nxtButton.addEventListener('click', () => {
            tabOffset++;
            rearrangeTabPanel();
        });

        rearrangeTabPanel();

        const debouncedRearrangeTabPanel = debounce(rearrangeTabPanel, 300);

        const resizeObserver = new ResizeObserver(() => {
            debouncedRearrangeTabPanel();
        });

        resizeObserver.observe(tabPanelButtons);
        window.addEventListener('resize', () => debouncedRearrangeTabPanel());

        /**
         * Verschiebt einen Tab in den sichtbaren Bereich
         * @param tabId - Die ID des zu verschiebenden Tabs
         */
        function moveToVisibleArea(tabId: string): void {
            let i = 0;
            const tab = document.getElementById(tabId);
            if (!tab) {
                return;
            }

            const indexStr = tab.dataset.index;
            if (!indexStr) {
                console.warn(`Tab with id "${tabId}" has no data-index attribute`);
                return;
            }

            const index = parseInt(indexStr, 10);

            while (i < 100 && tab.style.display === 'none') {
                if (index < tabOffset) {
                    tabOffset--;
                } else if (index + 1 > tabOffset + tabLimit) {
                    tabOffset++;
                } else {
                    console.error('tab could not be moved to visible area');
                    break;
                }
                rearrangeTabPanel();
                i++;
            }
        }

        let focusedTabId: string | null = null;
        let activeTabId: string | null = null;
        let isInitialized = false;

        /**
         * Beobachtet Änderungen am Focus und Selected-Status von Tabs
         */
        const watchFocusedChange: MutationCallback = (mutations) => {
            const oldFocusedTabId = focusedTabId;
            focusedTabId = null;
            const oldActiveTabId = activeTabId;
            activeTabId = null;

            mutations.forEach((mutation) => {
                const target = mutation.target as HTMLElement;

                if ('focused' in target.dataset && target.dataset.focused === 'true') {
                    focusedTabId = target.id;
                }

                if (target.ariaSelected === 'true') {
                    activeTabId = target.id;
                }

                if (mutation.type === 'childList' && focusedTabId === null) {
                    mutation.addedNodes.forEach(node => {
                        if (node instanceof HTMLElement && node.classList.contains('tab')) {
                            rearrangeTabPanel();
                            moveToVisibleArea(node.id);
                        }
                    });
                }
            });

            // Setze isInitialized nach dem ersten Durchlauf
            if (!isInitialized) {
                isInitialized = true;
                return;
            }

            if (focusedTabId !== null && focusedTabId !== oldFocusedTabId && oldFocusedTabId !== null) {
                moveToVisibleArea(focusedTabId);
                document.getElementById(focusedTabId)?.focus();
            } else if (activeTabId !== null && activeTabId !== oldActiveTabId) {
                moveToVisibleArea(activeTabId);
            }
        };

        const mutationObserver = new MutationObserver(watchFocusedChange);

        const config: MutationObserverInit = {
            subtree: true,
            childList: true,
            attributes: true
        };

        mutationObserver.observe(tabPanelButtons, config);
    }
};

export default TabPanelModule;
